import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { EventIcon } from "@faire/retailer-visitor-shared/components/Events/shared/EventIcon";
import { useEventsStore } from "@faire/retailer-visitor-shared/stores/domain/Events/shared/EventsStore";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Row } from "@faire/web--source/slate/Layout";
import { getEventTheme } from "@faire/web--source/slate/Theme/eventsThemeColors";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Tooltip } from "@faire/web--source/slate/Tooltip";
import { Typography } from "@faire/web--source/slate/Typography";
import { ICurrentPromotionalEventResponse } from "@faire/web-api--source/indigofair/data/ICurrentPromotionalEventResponse";
import { observer } from "mobx-react";
import React from "react";
import styled, { css } from "styled-components";
var EventsThemeBadgeBase = function(param) {
    var isExpandable = param.expandable, className = param.className, showToolTip = param.showToolTip;
    var strictLocalize = useStrictLocalization().strictLocalize;
    var _useEventsStore = useEventsStore([
        "nextOrActiveEventTheme",
        "nextOrActive"
    ]), nextOrActiveEventTheme = _useEventsStore.nextOrActiveEventTheme, nextOrActiveEvent = _useEventsStore.nextOrActive;
    var eventsThemeColors = getEventTheme("faireMarket");
    var currentEventBadgeDetails = function() {
        var _nextOrActiveEvent_event;
        switch(nextOrActiveEvent === null || nextOrActiveEvent === void 0 ? void 0 : (_nextOrActiveEvent_event = nextOrActiveEvent.event) === null || _nextOrActiveEvent_event === void 0 ? void 0 : _nextOrActiveEvent_event.event_group){
            case ICurrentPromotionalEventResponse.IPromotionalEvent.PromotionalEventGroup.MARKET:
            default:
                return {
                    flyoutText: strictLocalize({
                        id: "GlfaKp",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Market special"
                            }
                        ]
                    }),
                    flyoutTextColor: eventsThemeColors.promoText,
                    hoverBackgroundColor: eventsThemeColors.badgeBackground,
                    hasBorder: true
                };
        }
    };
    var eventIcon = nextOrActiveEventTheme.eventIcon;
    var BadgeComponent = eventIcon === null || eventIcon === void 0 ? void 0 : eventIcon.Component;
    if (!BadgeComponent) {
        return null;
    }
    var _currentEventBadgeDetails = currentEventBadgeDetails(), flyoutText = _currentEventBadgeDetails.flyoutText, flyoutTextColor = _currentEventBadgeDetails.flyoutTextColor, hoverBackgroundColor = _currentEventBadgeDetails.hoverBackgroundColor, hasBorder = _currentEventBadgeDetails.hasBorder;
    var backgroundColor = eventIcon === null || eventIcon === void 0 ? void 0 : eventIcon.backgroundColor;
    var innerBody = /*#__PURE__*/ _jsxs(Wrapper, {
        $animated: isExpandable && !showToolTip,
        $hoverBackgroundColor: hoverBackgroundColor,
        "data-test-id": "events-theme-badge",
        children: [
            /*#__PURE__*/ _jsx(InnerBorder, {
                $backgroundColor: backgroundColor,
                $hasBorder: hasBorder,
                className: "inner-border"
            }),
            /*#__PURE__*/ _jsx(BadgeSvg, {
                customIcon: BadgeComponent,
                size: "32px"
            }),
            isExpandable && !showToolTip ? /*#__PURE__*/ _jsx(OffsetContainer, {
                children: /*#__PURE__*/ _jsx(FlyoutTextContainer, {
                    className: "flyout-text-container",
                    children: /*#__PURE__*/ _jsx(FlyoutText, {
                        color: flyoutTextColor,
                        children: flyoutText
                    })
                })
            }) : null
        ]
    });
    var getEventThemeBody = function() {
        if (isExpandable && showToolTip) {
            return /*#__PURE__*/ _jsx(Tooltip, {
                targetNode: innerBody,
                contents: [
                    {
                        body: flyoutText
                    }
                ],
                width: 280,
                pointerOffset: -80
            });
        }
        return innerBody;
    };
    return /*#__PURE__*/ _jsx(AbsoluteWrapper, {
        className: className,
        children: getEventThemeBody()
    });
};
export var EventsThemeBadge = observer(EventsThemeBadgeBase);
var AbsoluteWrapper = styled.div.withConfig({
    componentId: "sc-57d26d69-0"
})([
    "position:absolute;"
]);
var OffsetContainer = styled(Row).withConfig({
    componentId: "sc-57d26d69-1"
})([
    "overflow:hidden;height:32px;left:14px;width:fit-content;"
]);
var FlyoutText = styled(Typography).attrs({
    variant: "paragraphSansMedium"
}).withConfig({
    componentId: "sc-57d26d69-2"
})([
    "&&&{font-size:12px;padding:6px 10px 6px 0;}"
]);
var InnerBorder = styled.div.withConfig({
    componentId: "sc-57d26d69-3"
})([
    "",
    " border-radius:50px;position:absolute;background:",
    ";width:32px;height:inherit;transition:width 0.5s ease-in-out;"
], function(param) {
    var $hasBorder = param.$hasBorder;
    return $hasBorder ? css([
        "box-shadow:inset 0 0 0 1px ",
        ";"
    ], Core.surface.subdued) : null;
}, function(param) {
    var $backgroundColor = param.$backgroundColor;
    return $backgroundColor;
});
var BadgeSvg = styled(EventIcon).withConfig({
    componentId: "sc-57d26d69-4"
})([
    "height:32px;width:32px;padding:7px;border-radius:100px;transition:transform 0.5s cubic-bezier(0.82,0.2,0.41,1.46);z-index:1;"
]);
var Wrapper = styled(Row).attrs({
    align: "center"
}).withConfig({
    componentId: "sc-57d26d69-5"
})([
    "position:relative;width:32px;height:32px;transition:width 0.5s ease-in-out;",
    ""
], function(props) {
    return props.$animated ? css([
        "&:hover{width:100%;& > ",
        " > .flyout-text-container{opacity:1;transform:translateX(0);}& > ",
        "{transform:rotate(90deg);}& > .inner-border{background:",
        ";width:100%;transition:width 0.5s ease-in-out;}}"
    ], OffsetContainer, BadgeSvg, props.$hoverBackgroundColor) : "";
});
var FlyoutTextContainer = styled(Row).withConfig({
    componentId: "sc-57d26d69-6"
})([
    "user-select:none;white-space:nowrap;border-radius:0 80px 80px 0;height:32px;opacity:0;transform:translateX(-100%);",
    "{",
    ":hover &{opacity:1;transform:translateX(0);}}transition:opacity 0.5s ease-in-out,transform 0.5s ease-in-out;"
], responsiveMediaQueries.canHover, Wrapper);
